import isEqual from 'lodash/isEqual';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import { fetchShippingCarriers } from '../../ducks/shipping.duck';
import { apiBaseUrl } from '../../util/apiBaseUrl';
import { deleteShopPhotos as deleteShopPhotosApi } from '../../util/api';

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPLOAD_SHOP_PHOTO_REQUEST = 'app/ProfileSettingsPage/UPLOAD_SHOP_PHOTO_REQUEST';
export const UPLOAD_SHOP_PHOTO_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_SHOP_PHOTO_SUCCESS';
export const UPLOAD_SHOP_PHOTO_ERROR = 'app/ProfileSettingsPage/UPLOAD_SHOP_PHOTO_ERROR';
export const CLEAR_SHOP_PHOTO = 'app/ProfileSettingsPage/CLEAR_SHOP_PHOTO';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

export const DEFAULT_CARRIERS_REQUEST = 'app/ProfileSettingsPage/DEFAULT_CARRIERS_REQUEST';
export const DEFAULT_CARRIERS_LOADED = 'app/ProfileSettingsPage/DEFAULT_CARRIERS_LOADED';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  shopPhoto: null,
  shopPhotoUploadInProgress: false,
  shopPhotoUploadError: null,
  updateInProgress: false,
  updateProfileError: null,
  // The seller's default carriers as this page loaded them into the shipping editor: a
  // profile save sends the carriers only once they differ from these (null: not loaded).
  defaultCarriersAsLoaded: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    // shopPhoto is the photo picked (the original); its crop uploads without replacing it
    case UPLOAD_SHOP_PHOTO_REQUEST:
      return {
        ...state,
        shopPhoto: payload.crop
          ? state.shopPhoto
          : { file: payload.file, localPreviewUrl: payload.localPreviewUrl },
        shopPhotoUploadInProgress: true,
        shopPhotoUploadError: null,
      };
    case UPLOAD_SHOP_PHOTO_SUCCESS:
      return {
        ...state,
        shopPhoto: payload.crop ? state.shopPhoto : { ...state.shopPhoto, url: payload.url },
        shopPhotoUploadInProgress: false,
      };
    case UPLOAD_SHOP_PHOTO_ERROR:
      return {
        ...state,
        shopPhoto: payload.crop ? state.shopPhoto : null,
        shopPhotoUploadInProgress: false,
        shopPhotoUploadError: payload.error,
      };
    case CLEAR_SHOP_PHOTO:
      return { ...state, shopPhoto: null, shopPhotoUploadError: null };

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        shopPhoto: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    case DEFAULT_CARRIERS_REQUEST:
      return { ...state, defaultCarriersAsLoaded: null };
    case DEFAULT_CARRIERS_LOADED:
      return { ...state, defaultCarriersAsLoaded: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

/** Has the seller changed their default carriers since this page loaded them? */
export const carriersEditedSelector = state => {
  const asLoaded = state.ProfileSettingsPage.defaultCarriersAsLoaded;
  return !!asLoaded && !isEqual(asLoaded, state.shipping.groupedCarriers);
};

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

export const clearShopPhoto = () => ({
  type: CLEAR_SHOP_PHOTO,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

// ================ Thunk ================ //

// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
}

/**
 * Removes the shop's old photos from storage as they are replaced or removed. Never
 * blocks the save: a file left behind is only storage.
 */
export const deleteShopPhotos = urls => () => {
  const validUrls = (urls || []).filter(Boolean);
  if (!validUrls.length) return Promise.resolve();
  return deleteShopPhotosApi(validUrls).catch(() => {});
};

/**
 * Saves the profile form, then removes the shop photo files the profile pointed to
 * before (`previousPhotoUrls`) and no longer does. Nothing is removed when the save
 * fails, and a file the saved profile still uses is never removed.
 */
export const updateProfileAndShopPhotos = (values, previousPhotoUrls = []) => dispatch =>
  dispatch(updateProfile(values)).then(result => {
    if (result?.id) {
      const { shopPhotoUrl, shopPhotoOriginalUrl } = values.publicData || {};
      const stillUsed = [shopPhotoUrl, shopPhotoOriginalUrl].filter(Boolean);
      dispatch(deleteShopPhotos(previousPhotoUrls.filter(url => !stillUsed.includes(url))));
    }
    return result;
  });

/**
 * Uploads a shop photo: the one picked (kept as the page's `shopPhoto`, the original), or,
 * with `crop`, its crop as the profile saves. Resolves with the file's URL; a failure is
 * kept in shopPhotoUploadError and rethrown.
 */
export function uploadShopPhoto(file, { crop = false } = {}) {
  return dispatch => {
    const localPreviewUrl = crop ? null : URL.createObjectURL(file);
    dispatch({ type: UPLOAD_SHOP_PHOTO_REQUEST, payload: { file, localPreviewUrl, crop } });

    const formData = new FormData();
    formData.append('file', file);

    return fetch(`${apiBaseUrl()}/api/upload/shop-photo`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    })
      .then(res => res.json())
      .then(data => {
        if (!data.success) throw new Error(data.message || 'Upload failed');
        dispatch({ type: UPLOAD_SHOP_PHOTO_SUCCESS, payload: { url: data.url, crop } });
        return data.url;
      })
      .catch(e => {
        dispatch({ type: UPLOAD_SHOP_PHOTO_ERROR, payload: { error: storableError(e), crop } });
        throw e;
      });
  };
}

export const updateProfile = actionPayload => {
  return (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        dispatch(updateProfileSuccess(response));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));
        return currentUser;
      })
      .catch(e => dispatch(updateProfileError(storableError(e))));
  };
};

/**
 * Loads the page: the current user, then the carriers editor filled with the seller's
 * saved default carriers (or the USPS default when none are saved).
 */
export const loadData = () => (dispatch, getState) =>
  dispatch(fetchCurrentUser()).then(() => {
    const currentUser = getState().user.currentUser;
    if (!currentUser?.id) return null;
    const defaultCarriers =
      currentUser.attributes?.profile?.privateData?.defaultShippingCarriers || null;
    dispatch({ type: DEFAULT_CARRIERS_REQUEST });
    return dispatch(fetchShippingCarriers({}, defaultCarriers)).then(() => {
      const { fetchCarrierError, groupedCarriers } = getState().shipping;
      if (!fetchCarrierError) {
        dispatch({ type: DEFAULT_CARRIERS_LOADED, payload: groupedCarriers });
      }
    });
  });
